import React, { useMemo } from "react";

import { compact, groupBy } from "lodash";
import { useElementSize } from "usehooks-ts";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { TileType, type TileFilter } from "@features/overview-v2/types";
import { PLATFORM_NORTHBEAM } from "@north-beam/nb-common";

import { FilterPill } from "./filter-pill";
import { MorePill } from "./more-pill";

const FILTER_PILL_WIDTH = 130;
const FILTER_MORE_WIDTH = 60;
const METRIC_EXPANDED_TILE_FILTERS_MAX = 10;

interface Props {
  tileType: TileType;
  filters: TileFilter[];
  isResizing: boolean;
  tileHeight: number;
}

export const FilterPills = ({
  tileType,
  filters: tileFilters,
  isResizing,
  tileHeight,
}: Props) => {
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const [setFiltersContainerRef, { width: filtersContainerWidth }] =
    useElementSize();

  const filters = useMemo(() => {
    const result: { breakdown: string; values: string[]; total: number }[] = [];
    const groupedFilters = groupBy(tileFilters, "breakdown");

    for (const [breakdown, filters] of Object.entries(groupedFilters)) {
      const breakdownConfig = breakdownConfigs.find((e) => e.key === breakdown);

      if (breakdownConfig) {
        const values = compact(
          filters.map((e) =>
            breakdownConfig.choices.find((c) => c.value === e.value),
          ),
        ).map((e) => e.label);

        result.push({
          breakdown: breakdownConfig.name,
          values,
          total: breakdownConfig.choices.length,
        });
      } else {
        const values = filters.map((e) => e.value);
        result.push({
          breakdown,
          values,
          total: values.length,
        });
      }
    }

    return result.sort((e) => (e.breakdown === PLATFORM_NORTHBEAM ? -1 : 0));
  }, [tileFilters, breakdownConfigs]);

  const filtersThreshold =
    tileType === TileType.METRIC && tileHeight === 2 && !isResizing
      ? METRIC_EXPANDED_TILE_FILTERS_MAX
      : Math.floor(
          (filtersContainerWidth - FILTER_MORE_WIDTH) / FILTER_PILL_WIDTH,
        );

  const visibleFilters = filters.slice(0, filtersThreshold);
  const moreFilters = filters.slice(filtersThreshold);

  return (
    <div ref={setFiltersContainerRef} className="flex flex-wrap gap-2">
      {visibleFilters.map((filter) => (
        <FilterPill key={filter.breakdown} filter={filter} />
      ))}
      {moreFilters.length > 0 && <MorePill filters={moreFilters} />}
    </div>
  );
};
